import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Typography, Box, CircularProgress } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import clsx from "clsx";
import { PATHS } from "util/appConstants";
import { getAllPermissions, selectPermissions, selectPermissionsStatus } from "redux/slices/permissionsSlice";
import Navbar from "components/Navbar";
import DarkLayout from "components/Shared/DarkLayout";
import CheckIcon from "@material-ui/icons/Check";
import XIcon from "@material-ui/icons/Clear";
import { getUserById, selectSelectedUser, selectUser, selectUsersStatus } from "redux/slices/userSlice";
import AdminBar from "components/Masterbar/AdminBar";
import PermissionsManager from "components/Permissions/PermissionsManager";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
    marginRight: "32px",
  },
  _basicdetailhead: {
    display: "flex",
    marginTop: "41px",
  },
  _basicdetail: {
    display: "flex",
  },
  _detail: {
    color: "#F5F5F5",
    marginRight: "32px",
    font: "normal normal normal 18px/24px Roboto",
  },
  _name: {
    letterSpacing: "0px",
  },
  _margintop80: {
    marginTop: "80px",
  },
  _adressbox: {
    marginTop: "24px",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
});

const UserDetail = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const loading = useSelector(selectUsersStatus);
  const user = useSelector(selectSelectedUser);
  const loggedUser = useSelector(selectUser);
  const permissions = useSelector(selectPermissions);
  const permissionsLoading = useSelector(selectPermissionsStatus);

  useEffect(() => {
    dispatch(getAllPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (id && !loading) {
      dispatch(getUserById(id));
    }
  }, [dispatch, id]);

  const editHandler = () => {
    history.push(PATHS.admin.usersEdit.replace(":id", id));
  };

  const permissionsValueMap = useMemo(() => {
    const map = {};
    if (!permissions.length) return map;

    permissions.forEach((perm) => {
      map[perm.id] = false;
    });

    let userPerms = user?.permissions;
    if (typeof userPerms === "string") {
      try {
        userPerms = JSON.parse(userPerms);
      } catch (e) {
        userPerms = {};
      }
    }

    if (userPerms && typeof userPerms === "object") {
      permissions.forEach((perm) => {
        const modKey = (perm.module || "").toString().toLowerCase();
        if (userPerms[modKey] && userPerms[modKey][perm.name] !== undefined) {
          map[perm.id] = Boolean(userPerms[modKey][perm.name]);
        }
      });
    }

    const rolePerms =
      user?.Role?.Permissions ||
      user?.Role?.RoleHasPermissions ||
      user?.RoleHasPermissions ||
      [];

    rolePerms.forEach((perm) => {
      const permId = perm.permission_id !== undefined ? perm.permission_id : perm.id;
      const permValue = perm.value !== undefined ? Boolean(perm.value) : true;
      if (permId !== undefined && permId !== null) {
        map[permId] = permValue;
      }
    });

    return map;
  }, [permissions, user]);

  return (
    <>
      <Navbar />
      <AdminBar />
      <DarkLayout doublebar loading={loading || !user || permissionsLoading}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {user?.name}
          </Typography>
          {loggedUser?.permissions?.user?.update && (
            <Box className={classes._buttonbox} component="div">
              <EditIcon onClick={editHandler} className={classes._edit} />
              <Typography component="span" className={clsx(classes._edittext, "edittag")}>
                {t("Edit")}
              </Typography>
            </Box>
          )}
        </div>
        <Typography className={classes._infoheading} variant="h5">
          {t("Basic Data")}
        </Typography>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Account")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {user?.SupplierGroup?.name}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Name")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {user?.name}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Surname")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {user?.surname}
            </Typography>
          </div>
        </div>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Supplier")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {user?.Supplier?.name}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("Supplier Location")}
            </Typography>
            <Typography className={clsx(classes._detail, classes._width11)} variant="h6">
              {user?.SupplierLocation?.name}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("Role")}
            </Typography>
            <Typography className={clsx(classes._detail, classes._width11)} variant="h6">
              {user?.Role?.name}
            </Typography>
          </div>
        </div>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("E-mail")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {user?.email}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("Active")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {user?.active ? <CheckIcon /> : <XIcon />}
            </Typography>
          </div>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Permissions")}
        </Typography>

        {!permissions.length ? (
          <CircularProgress />
        ) : (
          <Box mt={2}>
            <PermissionsManager
              permissions={permissions}
              value={permissionsValueMap}
              disabled={true}
              darkMode={true}
            />
          </Box>
        )}
      </DarkLayout>
    </>
  );
};

export default UserDetail;
